import React, { useMemo, useState, useEffect, useRef } from 'react';
import classnames from 'classnames';
import { Layout, Justify, Button } from 'tea-component';
import SvgIcon from '@home/components/SvgIcon';
import CardSlot from '@gp/components/CardSlot';
import InfoBubble from '@gp/components/InfoBubble';
import GeneralDownloadBtn from '@gp/components/GeneralDownloadBtn';
import TimeChangeLegend from '@gp/components/TimeChangeLegend';
import Filter from './components/ProjectFilter';
import SearchCommon from '@gp/components/SearchCommon';
// @ts-ignore
import ProjectList from './components/ProjectList';
import DownloadModal from './components/DownloadModal';
import usePageLog from '@gp/hooks/usePageLog';
import usePageFilterInit from '@gp/hooks/usePageFilterInit';
// @ts-ignore
import useTeaTable from '@gp/hooks/useTeaTable';
import usePageStore from '@gp/hooks/usePageStore';
import useProjectInfoAccessable from './_hooks/useProjectInfoAccessable';
import useOptionsAndValues from './_hooks/useOptionsAndValues';
import {
  LOCKER_MODEL_NAMESPACE,
  DEFAULT_FILTER,
  DEFAULT_SORT,
  DEFAULT_SEARCH,
  ILockerData,
} from './models/locker';
import { getProjectList } from '@gp/services/project';
import { toProjectDetails } from '@gp/utils/project';
import {
  formatSearchProjectsResults,
  analysisLockerData,
  formatFilterParams,
  formatSearchParams,
} from './utils';
import { SortType } from '@gp/enums/common';
import { SEARCH_SELECT_COMP_AUTO_CLOSE_WHEN_SCROLL_NODE } from '@gp/constants/classNames';
import '@gp/assets/css/pages/pipelineProjectlist.scss';
import { useMobileDetailShortLink } from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Stats/_hook';
import { deleteFilterAllValue } from '@gp/utils/utils';
import { KEYS_MAPPING, NAMES_MAPPING } from '@gp/constants/field';

const { Content } = Layout;

const PipelineProjectList: React.FC = () => {
  const {
    loading: filterConditionsLoading,
    options: filterConditions,
    values: filterValues,
    defaultValues: allValuesInterface,
  } = useOptionsAndValues();
  const scrollBody = useRef(null);
  const { lockerData, setLockerData } = usePageStore<ILockerData>(
    LOCKER_MODEL_NAMESPACE,
  );
  const [selectedValue, setSelectedValue] = useState<any>({});
  const [searchList, setSearchList] = useState([]);
  const [searchVal, setSearchVal] = useState('');
  const headerTitle = 'Project List';
  const currentPage = 'projectList';
  const showRightBtn = true;

  const {
    lock,
    filter: lockerFilter,
    ppsViewVisible: lockerPPSViewVisible,
    sort: lockerSort,
    search: lockerSearch,
  } = analysisLockerData(lockerData);

  const [isOverFiler, setIsOverFiler] = useState(true);

  const fields = [
    {
      key: KEYS_MAPPING.CASCADE_PROJECT,
      type: 'cascadeProject',
      label: NAMES_MAPPING.CASCADE_PROJECT,
    },
    {
      key: KEYS_MAPPING.PLATFORM,
      type: 'selectMultiple',
      label: NAMES_MAPPING.PLATFORM,
    },
    {
      key: KEYS_MAPPING.MILESTONE,
      label: 'Current Milestone',
      saveKey: 'cur_milestone',
    },
    {
      key: KEYS_MAPPING.NEXT_MILESTONE,
      type: 'selectMultiple',
      label: NAMES_MAPPING.NEXT_MILESTONE,
    },
    {
      key: KEYS_MAPPING.PUBLISHER,
      type: 'selectMultiple',
      label: NAMES_MAPPING.PUBLISHER,
    },
    {
      key: KEYS_MAPPING.STATUS,
      type: 'selectMultiple',
      label: NAMES_MAPPING.STATUS,
    },
    {
      key: KEYS_MAPPING.STAGE,
      type: 'selectMultiple',
      label: NAMES_MAPPING.STAGE,
    },
  ];

  const {
    loading: initialLoading,
    filter: initialFilter,
    onUpdateShortUrlParams,
  } = usePageFilterInit({
    loading: filterConditionsLoading,
    conditions: {
      filter: filterConditions,
    },
    storeValues: {
      lock,
      filter: lockerFilter,
    },
    defaultValues: {
      filter: { ...DEFAULT_FILTER, project_id: filterValues?.project_id || [] },
    },
    allFilterOptionsValues: allValuesInterface,
    fields,
    setSelectedValue,
  });

  const defaultValuesCur = useMemo(() => {
    return {
      ...DEFAULT_FILTER,
      project_id: filterValues?.project_id || [],
    };
  }, [DEFAULT_FILTER, filterValues]);

  // 筛选器参数
  const [filter, setFilter] = useState<IPipelineProjectListFilterVO>(
    initialFilter as any,
  );
  // 排序参数
  const [sort, setSort] = useState(lock ? lockerSort : DEFAULT_SORT);

  // 搜索参数
  const [searchData, setSearchData] = useState(
    lock ? lockerSearch : DEFAULT_SEARCH,
  );

  const getProjectListWithHandledParams = (params: any) => {
    const {
      is_reset: isReset,
      sort_type: sortType,
      sort,
      search_type: searchType,
      search,
      search_id,
      searchVal,
      searchList,
      ...restParams
    } = params || {};

    const filterTmp = { ...filter, ...restParams };
    const filterParams = formatFilterParams(filterTmp, filterConditions);
    const searchParams = formatSearchParams({
      ...searchData,
      search_type: searchType,
      search,
      searchVal,
      searchList,
    });
    if (isReset) {
      setFilter(filterTmp);
    }

    // @ts-ignore
    return getProjectList({
      ...filterParams,
      ...searchParams,
      sort_type: sortType || SortType.None,
      sort: sort || '',
      search_id,
      status: [],
    });
  };

  const { tableProps, search, error, refresh, data, loading } = useTeaTable(
    getProjectListWithHandledParams,
    {
      manual: true,
      clientPaging: true, // 客户端分页
      defaultPageSize: 50,
      debounceWait: 0,
      tableListFieldName: 'items',
      finalFormConfig: {
        initialValues: initialFilter || DEFAULT_FILTER,
      },
      initialSorter: [
        {
          order: sort?.sort_type || DEFAULT_SORT.sort_type,
          by: sort?.sort || DEFAULT_SORT.sort,
        },
      ],
      defaultParams: {},
      onSuccess: () => {},
      onSortsChange: (values: any) => {
        // 同时只支持一个字段排序
        if (!!values?.length) {
          const firstSort = values[0] || {};
          const { order, by } = firstSort || {};
          const sortTmp = { sort_type: order, sort: by };
          setSort(sortTmp);
          setLockerData({ sort: sortTmp });
          return [firstSort];
        }
        const sortTmp = { sort_type: SortType.None, sort: '' };
        setSort(sortTmp);
        setLockerData({ sort: sortTmp });
        return [{}];
      },
      onSortsRequest: (sorts: any) => {
        // 修改请求时排序相关的参数
        if (!!sorts?.length) {
          const { order, by } = sorts[0] || {};
          return { sort_type: order, sort: by };
        }
        return {};
      },
    },
  );

  const { showRight, ppsViewable, ppsEditable, updatesViewable } =
    useProjectInfoAccessable({
      loading,
      data,
    });

  const [projectListTableLoading, setProjectListTableLoading] = useState(true);
  // 控制status和pps_comment显示隐藏
  const [ppsViewVisible, setPPSViewVisible] = useState(
    lock ? lockerPPSViewVisible : false,
  );
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);

  // 刷新搜索组件弹出面板位置的依赖数组
  const updateDeps = useMemo<boolean[]>(
    () => [loading, filterConditionsLoading, projectListTableLoading],
    [loading, filterConditionsLoading, projectListTableLoading],
  );

  // 列表数据接口查询参数（提供给下载用）
  const fetchParams = useMemo<IPipelineProjectListFetchParams>(() => {
    const filterParams = formatFilterParams(filter, filterConditions);
    const searchParams = formatSearchParams(searchData);
    return { ...filterParams, ...sort, ...searchParams };
  }, [filterConditions, filter, sort, searchData]);

  const handleTogglePPSVisible = () => {
    const ppsViewVisibleTmp = !ppsViewVisible;
    setPPSViewVisible(ppsViewVisibleTmp);
    setLockerData({ ppsViewVisible: ppsViewVisibleTmp });
  };

  const handleGoToProjectDetails = (payload: any, isJump = true) => {
    const { project_id: id, name } = payload || {};
    return toProjectDetails({ id, name }, null, isJump);
  };

  const handleSearch = (payload: any, searchList: any) => {
    const {
      search_type: searchType,
      search: _search,
      search_keyword: searchKeyword,
      search_ids,
    } = payload || {};
    const searchDataTmp = {
      search_type: searchType || '',
      search: _search || [],
      searchVal: searchKeyword || '',
      searchList: searchList || [],
      search_id: search_ids,
    };
    setSearchData(searchDataTmp);
    setLockerData({ search: searchDataTmp });
    search?.submit({ ...filter, ...sort, ...searchDataTmp });
  };

  const handleReset = () => {
    const searchTmp = DEFAULT_SEARCH;
    setSearchData(searchTmp);
    setLockerData({ search: searchTmp });
    search?.submit({
      ...defaultValuesCur,
      is_reset: true,
      ...sort,
      ...searchTmp,
    });
    onUpdateShortUrlParams({ filter: defaultValuesCur });
    setSelectedValue({});
    deleteFilterAllValue();
  };

  const setSearchListHandle = (list: any) => {
    const searchDataTmp = {
      ...searchData,
      searchList: list || [],
    };
    setSearchData(searchDataTmp);
  };
  const setSearchValHandle = (val: any) => {
    const searchDataTmp = {
      ...searchData,
      searchVal: val || '',
    };
    setSearchData(searchDataTmp);
  };

  const handleFilterChange = (payload: IPipelineProjectListFilterVO) => {
    const filterTmp = { ...filter, ...payload };
    setFilter(filterTmp);
    setLockerData({ filter: filterTmp });
    search?.submit({ ...filterTmp, ...sort, ...searchData });
    onUpdateShortUrlParams({ filter: filterTmp });
  };

  useEffect(() => {
    if (initialLoading) return;
    setFilter(initialFilter as any);
    search?.submit({ ...initialFilter, ...sort, ...searchData });
    onUpdateShortUrlParams({ filter: initialFilter });
  }, [initialLoading, initialFilter]);

  // 打点
  usePageLog({ pageId: 'PIPELINE_PROJECT_LIST' });

  return (
    <Content
      className={classnames(
        'dbg-page dbg-pipline-allprojects dbg-pipeline-project-list dbg-page-scroll-body',
        SEARCH_SELECT_COMP_AUTO_CLOSE_WHEN_SCROLL_NODE,
        {
          'is-need-top': !isOverFiler,
        },
      )}
      ref={scrollBody}
    >
      <Content.Header className="dbg-header" title={headerTitle} />
      <Content.Body
        className="gpProject dbg-pipline-allprojects-content dbg-pipeline-table-card-body-bottom0px"
        full
      >
        <div id="scroll-content" className="dbg-project-list-table font14">
          <Justify
            className="dbg-pipeline-justify-container"
            right={
              <div className="dbg-pipeline-justify-container-right">
                <div
                  style={{
                    display: showRight ? 'flex' : 'none',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                  }}
                >
                  <SearchCommon
                    placeholder="Search any project in the list"
                    showRightBtn={showRightBtn}
                    currentPage={currentPage}
                    formatSearchResults={formatSearchProjectsResults}
                    updateDeps={updateDeps}
                    searchVal={searchData?.searchVal}
                    searchList={searchData?.searchList}
                    onRightBtnClick={handleGoToProjectDetails}
                    submit={handleSearch}
                    setSearchList={setSearchListHandle}
                    setSearchVal={setSearchValHandle}
                  />
                  {/* {ppsViewable && ( */}
                  <InfoBubble
                    className="globalpedia-modal-font14"
                    isArrowPointAtCenter={false}
                    placementOffset={[5, 10]}
                    customBubbleContent={
                      <div style={{ whiteSpace: 'nowrap' }}>
                        {ppsViewVisible
                          ? 'Conceal DPS Comment'
                          : 'Show DPS Comment'}
                      </div>
                    }
                    DomOrText={
                      <Button
                        className="dbg-globalpedia-eyes ns-btn--icon"
                        type="weak"
                        onClick={handleTogglePPSVisible}
                      >
                        <SvgIcon
                          type={ppsViewVisible ? 'open-eyes' : 'eyes-closed'}
                        />
                      </Button>
                    }
                  />
                  {/* )} */}
                  <GeneralDownloadBtn
                    tipsPlacement="top-end"
                    tips={
                      !data?.items?.length
                        ? 'There is no data downloadable'
                        : undefined
                    }
                    isLoading={isDownloading}
                    isDisabled={!data?.items?.length}
                    onClick={() => {
                      setDownloadModalVisible(true);
                    }}
                  />
                </div>
              </div>
            }
          />
          <Filter
            loading={filterConditionsLoading}
            data={filterConditions as any}
            value={filter}
            onChange={handleFilterChange}
            allFilterOptionsValues={allValuesInterface}
            onReset={handleReset}
            setIsOverFiler={setIsOverFiler}
            scrollBody={scrollBody}
            headerTitle={headerTitle}
            showRightBtn={showRightBtn}
            currentPage={currentPage}
            formatSearchResults={formatSearchProjectsResults}
            updateDeps={updateDeps}
            searchVal={searchData?.searchVal}
            searchList={searchData?.searchList}
            handleSearch={handleSearch}
            selectedValue={selectedValue}
            setSelectedValue={setSelectedValue}
            setSearchList={setSearchListHandle}
            setSearchVal={setSearchValHandle}
          />

          <div className={'dbg-pipeline-project-list__card-topbar'}>
            <TimeChangeLegend
              tips="Milestone updated in the past 30 days."
              bubbleProps={{ placement: 'top-end' }}
              style={{ marginBottom: '10px' }}
            />
          </div>
          <CardSlot className="dbg-pipline-allprojects-table-card dbg-pipline-allprojects-table-card-no-after">
            <ProjectList
              tableProps={{
                ...tableProps,
                error,
                isEditStatus: false,
                projectListTableLoading,
                setProjectListTableLoading,
              }}
              againRequest={{
                filterValues,
                searchValues: {
                  search_type: searchData?.search_type,
                  search: searchData?.search || [],
                },
                search,
                refresh,
              }}
              ppsViewable={ppsViewable}
              updatesViewable={updatesViewable}
              ppsViewVisible={ppsViewVisible}
              ppsEditable={ppsEditable}
              setLockerData={setLockerData}
              handleReset={handleReset}
            />
          </CardSlot>
        </div>
      </Content.Body>

      {/* 下载弹窗 */}
      <DownloadModal
        ppsViewable={ppsViewable}
        updatesViewable={updatesViewable}
        setIsDownloading={setIsDownloading}
        downloadModalVisible={downloadModalVisible}
        setDownloadModalVisible={setDownloadModalVisible}
        fetchParams={fetchParams}
      />
    </Content>
  );
};

export default PipelineProjectList;
